window.strichwerke = window.strichwerke || {};

/**
 * Predictive search.
 *
 * Fetches /search/suggest.json as the visitor types and renders product
 * cards with large square imagery into the header overlay. Falls back to
 * the native /search results page on submit.
 */
strichwerke.Search = {
  overlay: null,
  input: null,
  resultsEl: null,
  idleEl: null,
  emptyEl: null,
  allLink: null,
  debounceTimer: null,
  abortController: null,
  lastQuery: "",

  DEBOUNCE_MS: 220,
  MIN_CHARS: 2,
  LIMIT: 6,

  init: function () {
    this.overlay = document.querySelector("[data-search-overlay]");
    if (!this.overlay) return;

    this.input = this.overlay.querySelector("[data-search-input]");
    this.resultsEl = this.overlay.querySelector("[data-search-results]");
    this.idleEl = this.overlay.querySelector("[data-search-idle]");
    this.emptyEl = this.overlay.querySelector("[data-search-empty]");
    this.allLink = this.overlay.querySelector("[data-search-all]");

    this.bindTriggers();
    this.bindOverlay();
    this.bindInput();
  },

  bindTriggers: function () {
    document.querySelectorAll("[data-search-toggle]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        this.open();
      });
    });
  },

  bindOverlay: function () {
    this.overlay.querySelectorAll("[data-search-close]").forEach((el) => {
      el.addEventListener("click", () => this.close());
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !this.overlay.hidden) this.close();
      // "/" opens search when not already typing in a field
      if (e.key === "/" && this.overlay.hidden) {
        const tag = (document.activeElement && document.activeElement.tagName) || "";
        if (tag !== "INPUT" && tag !== "TEXTAREA") {
          e.preventDefault();
          this.open();
        }
      }
    });
  },

  bindInput: function () {
    this.input.addEventListener("input", () => {
      const q = this.input.value.trim();
      clearTimeout(this.debounceTimer);

      if (q.length < this.MIN_CHARS) {
        this.lastQuery = "";
        this.showIdle();
        return;
      }

      this.debounceTimer = setTimeout(() => this.fetch(q), this.DEBOUNCE_MS);
    });

    // Keep the "view all" link in sync with the current query
    this.input.addEventListener("input", () => {
      const q = this.input.value.trim();
      if (this.allLink) {
        const base = this.allLink.dataset.baseUrl || this.allLink.getAttribute("href").split("?")[0];
        this.allLink.dataset.baseUrl = base;
        this.allLink.setAttribute(
          "href",
          q ? base + "?q=" + encodeURIComponent(q) + "&type=product" : base
        );
      }
    });
  },

  open: function () {
    this.overlay.hidden = false;
    document.body.style.overflow = "hidden";
    // Defer focus so the transition doesn't fight the caret
    requestAnimationFrame(() => this.input && this.input.focus());
  },

  close: function () {
    this.overlay.hidden = true;
    document.body.style.overflow = "";
    if (this.abortController) this.abortController.abort();
  },

  showIdle: function () {
    if (this.idleEl) this.idleEl.hidden = false;
    if (this.resultsEl) this.resultsEl.hidden = true;
    if (this.emptyEl) this.emptyEl.hidden = true;
  },

  fetch: function (query) {
    if (query === this.lastQuery) return;
    this.lastQuery = query;

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    const url =
      "/search/suggest.json?q=" +
      encodeURIComponent(query) +
      "&resources[type]=product&resources[limit]=" +
      this.LIMIT;

    fetch(url, { signal: this.abortController.signal })
      .then((res) => res.json())
      .then((data) => {
        const products =
          (data.resources &&
            data.resources.results &&
            data.resources.results.products) ||
          [];
        this.render(products);
      })
      .catch((err) => {
        if (err && err.name === "AbortError") return;
        this.render([]);
      });
  },

  render: function (products) {
    if (this.idleEl) this.idleEl.hidden = true;

    if (!products.length) {
      if (this.resultsEl) this.resultsEl.hidden = true;
      if (this.emptyEl) this.emptyEl.hidden = false;
      return;
    }

    if (this.emptyEl) this.emptyEl.hidden = true;
    this.resultsEl.innerHTML = products.map((p) => this.card(p)).join("");
    this.resultsEl.hidden = false;
  },

  card: function (product) {
    const img = product.featured_image || {};
    const src = img.url || product.image || "";
    const alt = img.alt || product.title || "";
    const price = this.money(product.price);

    // Shopify CDN accepts &width= on the returned URL; 400px covers a
    // ~200px rendered square at 2x.
    const srcset = src
      ? [
          this.sized(src, 200) + " 200w",
          this.sized(src, 400) + " 400w",
          this.sized(src, 600) + " 600w",
        ].join(", ")
      : "";

    const media = src
      ? `<img src="${this.sized(src, 400)}" srcset="${srcset}" sizes="(max-width: 768px) 50vw, 33vw" alt="${this.escape(
          alt
        )}" width="400" height="400" loading="lazy">`
      : `<span class="search-card__placeholder">${this.escape(
          (product.title || "").slice(0, 12)
        )}</span>`;

    return `
      <a href="${product.url}" class="search-card">
        <div class="search-card__media">${media}</div>
        <div class="search-card__info">
          <h3 class="search-card__title">${this.escape(product.title || "")}</h3>
          <span class="search-card__price">${price}</span>
        </div>
      </a>`;
  },

  sized: function (url, width) {
    if (!url) return "";
    return url + (url.indexOf("?") > -1 ? "&" : "?") + "width=" + width;
  },

  money: function (value) {
    if (value === undefined || value === null) return "";
    const n = Number(value);
    if (isNaN(n)) return "";
    return (
      n.toLocaleString("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }) + " €"
    );
  },

  escape: function (str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  },
};

document.addEventListener("DOMContentLoaded", function () {
  strichwerke.Search.init();
});
